import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/components/ui/sonner";
import { playTone, stopAll, playBinaural } from "@/lib/audioEngine";
import { Heart, Play, Square, Star, Clock, Activity, Radio } from "lucide-react";
import CodesDirectConnect from "@/components/CodesDirectConnect";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const CAT_COLORS = {
  calm: "#00CC44",
  focus: "#0088FF",
  breathwork: "#FF6600",
  recovery: "#FF0000",
  sleep_prep: "#7733FF",
  creative_flow: "#FFD700",
};

export default function CodesPage() {
  const [bpm, setBpm] = useState(72);
  const [bpmInput, setBpmInput] = useState("72");
  const [recs, setRecs] = useState([]);
  const [allCats, setAllCats] = useState({});
  const [activeCat, setActiveCat] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingFreq, setPlayingFreq] = useState(null);
  const [timerSec, setTimerSec] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [view, setView] = useState("match"); // match | favorites | history

  const tapTimes = useRef([]);
  const nodesRef = useRef([]);
  const binauralRef = useRef(null);
  const timerRef = useRef(null);
  const sessionStartRef = useRef(null);

  // Load categories + favorites + sessions on mount
  useEffect(() => {
    axios.get(`${API}/codes/categories`).then(r => setAllCats(r.data)).catch(() => {});
    axios.get(`${API}/codes/favorites`).then(r => setFavorites(r.data)).catch(() => {});
    axios.get(`${API}/codes/sessions`).then(r => setSessions(r.data)).catch(() => {});
  }, []);

  // Timer tick
  useEffect(() => {
    if (timerActive) {
      timerRef.current = setInterval(() => setTimerSec(s => s + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [timerActive]);

  // Fetch recommendations when BPM changes
  const fetchRecs = useCallback(async (b) => {
    try {
      const r = await axios.get(`${API}/codes/match?bpm=${b}`);
      setRecs(r.data.recommendations || []);
      if (r.data.recommendations?.length) setActiveCat(r.data.recommendations[0].key);
    } catch { setRecs([]); }
  }, []);

  useEffect(() => { fetchRecs(bpm); }, [bpm, fetchRecs]);

  // Tap tempo
  const handleTap = () => {
    const now = Date.now();
    tapTimes.current.push(now);
    if (tapTimes.current.length > 8) tapTimes.current.shift();
    if (tapTimes.current.length >= 2) {
      const intervals = [];
      for (let i = 1; i < tapTimes.current.length; i++) intervals.push(tapTimes.current[i] - tapTimes.current[i - 1]);
      const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      const detected = Math.round(60000 / avg);
      if (detected >= 30 && detected <= 220) {
        setBpm(detected);
        setBpmInput(String(detected));
      }
    }
    // Reset if gap > 3s
    setTimeout(() => {
      if (Date.now() - tapTimes.current[tapTimes.current.length - 1] > 3000) tapTimes.current = [];
    }, 3100);
  };

  const applyManualBpm = () => {
    const v = Number(bpmInput);
    if (v >= 30 && v <= 220) { setBpm(v); }
    else toast.error("BPM must be 30–220");
  };

  // Audio playback
  const playFrequency = (hz, name, catKey) => {
    stopAll(); nodesRef.current = []; binauralRef.current = null;
    const node = playTone({ freq: hz, gain: 0.2, pan: 0 });
    nodesRef.current = [node];
    setIsPlaying(true);
    setPlayingFreq({ hz, name, category: catKey });
    setTimerSec(0);
    setTimerActive(true);
    sessionStartRef.current = Date.now();
    toast.success(`${hz} Hz — ${name}`);
  };

  const playBinauralSession = (cat) => {
    stopAll(); nodesRef.current = []; binauralRef.current = null;
    const b = cat.binaural;
    binauralRef.current = playBinaural({ carrierHz: b.carrier, beatHz: b.beat, seconds: 1800, gain: 0.25, offsetCents: 0, leftAdjust: 0, rightAdjust: 0 });
    setIsPlaying(true);
    setPlayingFreq({ hz: b.carrier, name: b.label, category: cat.key || activeCat });
    setTimerSec(0);
    setTimerActive(true);
    sessionStartRef.current = Date.now();
    toast.success(`Binaural: ${b.carrier} Hz carrier, ${b.beat} Hz beat`);
  };

  const stopPlayback = async () => {
    stopAll(); nodesRef.current = []; binauralRef.current = null;
    setIsPlaying(false);
    setTimerActive(false);
    // Save session
    if (sessionStartRef.current && timerSec > 5) {
      try {
        await axios.post(`${API}/codes/session`, {
          bpm, category: playingFreq?.category || "unknown",
          duration_seconds: timerSec,
          frequencies_used: playingFreq ? [{ hz: playingFreq.hz, name: playingFreq.name }] : [],
        });
        const r = await axios.get(`${API}/codes/sessions`);
        setSessions(r.data);
      } catch {}
    }
    setPlayingFreq(null);
  };

  const toggleFav = async (catKey, hz, name) => {
    try {
      const r = await axios.post(`${API}/codes/favorites`, { category: catKey, frequency_hz: hz, name });
      if (r.data.action === "added") toast.success(`Saved ${name}`);
      else toast.message(`Removed ${name}`);
      const f = await axios.get(`${API}/codes/favorites`);
      setFavorites(f.data);
    } catch {}
  };

  const isFav = (catKey, hz) => favorites.some(f => f.category === catKey && f.frequency_hz === hz);
  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const activeCatData = activeCat ? (recs.find(r => r.key === activeCat) || allCats[activeCat]) : null;

  return (
    <div className="mx-auto max-w-lg px-3 sm:px-4 py-4 space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-mono font-bold text-[#FF0000] tracking-widest" data-testid="codes-page-title">CODES</h1>
        <p className="text-[10px] font-mono tracking-widest text-[#666] uppercase">Rhythm-Based Wellness &#183; Self-Guided Exploration</p>
      </div>

      {/* BPM Input */}
      <Card className="sg-frame border-[#FF0000]/20 overflow-hidden" data-testid="codes-bpm-card">
        <CardContent className="p-4 space-y-3">
          <div className="text-center">
            <div className="text-[9px] tracking-[0.3em] text-[#666] uppercase mb-2">Your Rhythm</div>
            <div className="text-5xl font-mono font-bold text-[#FF0000] tracking-wider" data-testid="codes-bpm-display">
              {bpm}
            </div>
            <div className="text-xs font-mono text-[#666]">BPM</div>
          </div>

          <div className="flex gap-2 items-center justify-center">
            {/* Tap area */}
            <button
              onClick={handleTap}
              className="relative w-16 h-16 rounded-full border-2 border-[#FF0000]/40 bg-[#FF0000]/5 flex items-center justify-center hover:bg-[#FF0000]/15 active:scale-95 transition-all"
              data-testid="codes-tap-btn"
            >
              <Heart className="w-6 h-6 text-[#FF0000]" />
              <div className="absolute -bottom-4 text-[7px] font-mono text-[#666] tracking-widest">TAP</div>
            </button>

            <div className="text-[#444] text-xs font-mono">or</div>

            {/* Manual input */}
            <div className="flex items-center gap-1.5">
              <Input
                type="number" min="30" max="220" value={bpmInput}
                onChange={e => setBpmInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && applyManualBpm()}
                className="w-20 bg-black border-[#444] text-[#FF0000] text-center font-mono text-lg h-10"
                data-testid="codes-bpm-input"
              />
              <Button size="sm" onClick={applyManualBpm} className="bg-[#FF0000] text-black font-mono h-10 px-3 hover:bg-[#FF3333]" data-testid="codes-bpm-go">
                GO
              </Button>
            </div>
          </div>

          <div className="text-[8px] text-center font-mono text-[#444]">
            Tap your heartbeat or enter BPM &#183; For wellness exploration only
          </div>
        </CardContent>
      </Card>

      {/* Now Playing / Timer */}
      {isPlaying && (
        <Card className="sg-frame border-[#FF0000]/30 overflow-hidden" data-testid="codes-now-playing">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-2 h-2 rounded-full bg-[#FF0000] animate-pulse shrink-0" />
              <div className="min-w-0">
                <div className="text-xs font-mono text-[#FF0000] truncate">{playingFreq?.hz} Hz — {playingFreq?.name}</div>
                <div className="text-[9px] font-mono text-[#666]">{playingFreq?.category}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm font-mono text-[#C0C0C0]" data-testid="codes-timer">{fmtTime(timerSec)}</span>
              <Button size="sm" onClick={stopPlayback} className="bg-[#FF0000]/20 text-[#FF0000] border border-[#FF0000]/40 h-7 px-2 hover:bg-[#FF0000]/30" data-testid="codes-stop-btn">
                <Square className="w-3 h-3" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* View tabs */}
      <div className="flex border-b border-[#333]/40">
        {[
          { key: "match", label: "RECOMMENDED" },
          { key: "favorites", label: "FAVORITES" },
          { key: "history", label: "HISTORY" },
        ].map(v => (
          <button key={v.key} onClick={() => setView(v.key)}
            className={`flex-1 py-2 text-[10px] font-mono tracking-widest transition-all ${view === v.key ? "text-[#FF0000] border-b-2 border-[#FF0000]" : "text-[#555] hover:text-[#888] border-b-2 border-transparent"}`}
            data-testid={`codes-view-${v.key}`}>{v.label}</button>
        ))}
      </div>

      {/* ═══ RECOMMENDED ═══ */}
      {view === "match" && (
        <div className="space-y-3" data-testid="codes-match-view">
          {/* Category pills */}
          <div className="flex flex-wrap gap-1.5" data-testid="codes-cat-pills">
            {recs.map(r => (
              <button key={r.key} onClick={() => setActiveCat(r.key)}
                className={`px-3 py-1.5 text-[10px] font-mono tracking-wider border transition-all ${activeCat === r.key
                  ? `border-[${CAT_COLORS[r.key]}] text-[${CAT_COLORS[r.key]}]`
                  : "border-[#333]/50 text-[#888] hover:text-[#C0C0C0]"}`}
                style={activeCat === r.key ? { borderColor: CAT_COLORS[r.key], color: CAT_COLORS[r.key], background: `${CAT_COLORS[r.key]}10` } : {}}
                data-testid={`codes-cat-${r.key}`}>
                {r.label}
              </button>
            ))}
          </div>

          {/* Active category detail */}
          {activeCatData && (
            <div className="space-y-2">
              <div className="text-xs font-mono text-[#888]">{activeCatData.desc}</div>

              {/* Binaural session */}
              <button
                onClick={() => playBinauralSession({ ...activeCatData, key: activeCat })}
                className="w-full flex items-center justify-between p-3 border border-[#FF0000]/30 bg-[#FF0000]/5 hover:bg-[#FF0000]/10 transition-all"
                data-testid="codes-binaural-play"
              >
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[#FF0000]" />
                  <div className="text-left">
                    <div className="text-xs font-mono text-[#FF0000]">Binaural Session</div>
                    <div className="text-[9px] font-mono text-[#666]">{activeCatData.binaural?.label} &#183; {activeCatData.binaural?.carrier} Hz carrier</div>
                  </div>
                </div>
                <Play className="w-4 h-4 text-[#FF0000]" />
              </button>

              {/* Individual frequencies */}
              {activeCatData.frequencies?.map((f, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 border border-[#333]/40 bg-[#0a0a0c]" data-testid={`codes-freq-${f.hz}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <button onClick={() => playFrequency(f.hz, f.name, activeCat)} className="w-7 h-7 rounded-full border border-[#FF0000]/40 bg-[#FF0000]/10 flex items-center justify-center hover:bg-[#FF0000]/25 transition-all shrink-0">
                      <Play className="w-3 h-3 text-[#FF0000]" />
                    </button>
                    <div className="min-w-0">
                      <div className="text-xs font-mono text-[#C0C0C0]">{f.hz} Hz — {f.name}</div>
                      <div className="text-[8px] font-mono text-[#555]">{f.duration_min} min suggested</div>
                    </div>
                  </div>
                  <button onClick={() => toggleFav(activeCat, f.hz, f.name)} className="p-1.5 shrink-0" data-testid={`codes-fav-${f.hz}`}>
                    <Star className={`w-4 h-4 transition-colors ${isFav(activeCat, f.hz) ? "fill-[#FFD700] text-[#FFD700]" : "text-[#444] hover:text-[#888]"}`} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ FAVORITES ═══ */}
      {view === "favorites" && (
        <div className="space-y-2" data-testid="codes-fav-view">
          {favorites.length === 0 && (
            <div className="text-center py-8 text-xs font-mono text-[#444]">No favorites yet &#183; Star a frequency to save it</div>
          )}
          {favorites.map((f, i) => (
            <div key={i} className="flex items-center justify-between p-2.5 border border-[#333]/40 bg-[#0a0a0c]">
              <div className="flex items-center gap-2 min-w-0">
                <button onClick={() => playFrequency(f.frequency_hz, f.name, f.category)} className="w-7 h-7 rounded-full border border-[#FF0000]/40 bg-[#FF0000]/10 flex items-center justify-center hover:bg-[#FF0000]/25 shrink-0">
                  <Play className="w-3 h-3 text-[#FF0000]" />
                </button>
                <div className="min-w-0">
                  <div className="text-xs font-mono text-[#C0C0C0]">{f.frequency_hz} Hz — {f.name}</div>
                  <div className="text-[8px] font-mono text-[#555]" style={{ color: CAT_COLORS[f.category] }}>{f.category}</div>
                </div>
              </div>
              <button onClick={() => toggleFav(f.category, f.frequency_hz, f.name)} className="p-1.5 shrink-0">
                <Star className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ═══ HISTORY ═══ */}
      {view === "history" && (
        <div className="space-y-2" data-testid="codes-history-view">
          {sessions.length === 0 && (
            <div className="text-center py-8 text-xs font-mono text-[#444]">No sessions yet &#183; Play a frequency to begin</div>
          )}
          <ScrollArea className="h-[350px]">
            <div className="space-y-1.5 pr-2">
              {sessions.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-2 border border-[#333]/30 bg-[#0a0a0c] text-[10px] font-mono">
                  <div className="flex items-center gap-2 min-w-0">
                    <Clock className="w-3 h-3 text-[#555] shrink-0" />
                    <span className="text-[#C0C0C0]">{fmtTime(s.duration_seconds)}</span>
                    <span className="text-[#888]" style={{ color: CAT_COLORS[s.category] }}>{s.category}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#555] shrink-0">
                    <span>{s.bpm} BPM</span>
                    <span>{s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}</span>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* CODES Direct Connect — PTT / Clips / 96 */}
      <Card className="sg-frame border-[#FF0000]/20 overflow-hidden" data-testid="codes-page-direct-connect">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Radio className="w-4 h-4 text-[#FF0000]" />
            <span className="text-sm font-mono font-bold text-[#FF0000] tracking-wider">DIRECT CONNECT</span>
            <span className="text-[8px] font-mono text-[#FF0000]/30 tracking-widest">CODES by GODMODE LLC</span>
          </div>
          <CodesDirectConnect compact />
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <div className="text-[7px] font-mono text-[#333] text-center leading-relaxed pt-2">
        CODES is a self-guided audio tool for personal exploration. It does not diagnose, treat, or cure any condition. Listen at comfortable volumes. Consult a healthcare provider for medical concerns.
      </div>
    </div>
  );
}
